import { formatInstant, type InstantInput, type InstantPreset } from "./datetime";
import { FormatInputError } from "./errors";
import { formatMoneyMajor, formatMoneyMinor, minorToDecimalString } from "./money";
import { formatEnergy, type EnergyPreset } from "./quantity";

// Display rules for API values on top of the strict formatters: instants are
// shown in the viewer's browser zone (LOC-104 section 4), absent values stay
// null so callers keep their own missing/unavailable wording, and a value a
// formatter refuses is shown verbatim instead of being guessed (no implicit
// UTC, no implicit currency).

/** The browser's IANA zone: the web clients have no personal time-zone setting. */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** The formatted value, or undefined when a formatter refuses the input. */
function attempt<T>(format: () => T): T | undefined {
  try {
    return format();
  } catch (error) {
    if (error instanceof FormatInputError) return undefined;
    throw error;
  }
}

function shown(raw: unknown, format: () => string): string {
  return attempt(format) ?? String(raw);
}

const rawMoney = (amount: number | string, currency: string | null | undefined) => `${amount}${currency ? ` ${currency}` : ""}`;

/** An API instant in the viewer's zone; null when absent; an offset-less value (S-4) verbatim. */
export function viewerInstant(value: InstantInput | null | undefined, preset: InstantPreset = "dateTime"): string | null {
  if (value === null || value === undefined || value === "") return null;
  return shown(value, () => formatInstant(value, { timeZone: viewerTimeZone(), preset }));
}

/** Major-unit amount in the record's own currency; null when the amount is absent.
 *  Without a usable currency the amount is shown with whatever code was sent. */
export function moneyMajor(amount: number | null | undefined, currency: string | null | undefined): string | null {
  if (amount === null || amount === undefined) return null;
  return shown(rawMoney(amount, currency), () => formatMoneyMajor(amount, currency ?? ""));
}

/**
 * Fixed hundredths of the major unit, whatever the currency's ISO 4217 digits:
 * billing line items' `amount_cents` (PricebookBillingEngine._line multiplies
 * by 100, session_billing divides totals by 100). 123400 JPY is ¥1,234 and
 * 1234 KWD is KWD 12.340. Converted as an exact decimal, then shown in major units.
 * The scale does not depend on the currency, so without a usable one the
 * converted decimal is shown (1250 -> "12.50"); a non-integer amount is shown as sent.
 */
export function moneyHundredths(amount: number | null | undefined, currency: string | null | undefined): string | null {
  if (amount === null || amount === undefined) return null;
  const major = attempt(() => minorToDecimalString(amount, 2));
  if (major === undefined) return rawMoney(amount, currency);
  return shown(rawMoney(major, currency), () => formatMoneyMajor(major, currency ?? ""));
}

/** ISO 4217 minor units in the record's own currency. Only for fields with that contract:
 *  the billing `*_cents` fields are fixed hundredths (moneyHundredths). */
export function moneyMinor(amount: number | null | undefined, currency: string | null | undefined): string | null {
  if (amount === null || amount === undefined) return null;
  return shown(rawMoney(amount, currency), () => formatMoneyMinor(amount, currency ?? ""));
}

/** Watt-hours from the API as energy text; null when absent or not a number. */
export function energyFromWh(wh: number | null | undefined, preset: EnergyPreset = "summary"): string | null {
  if (typeof wh !== "number" || !Number.isFinite(wh)) return null;
  return formatEnergy(wh, "Wh", preset);
}
